import React, { useEffect, useState } from 'react';
import DashboardLayout from './DashboardLayout';
import { Card, Row, Col, ListGroup, Spinner, Alert, Button, Form, Badge, Modal } from 'react-bootstrap';
import { FaStar, FaReply, FaFlag } from 'react-icons/fa';
import { businessApi, reviewsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const DashboardReviews = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingReviewId, setReplyingReviewId] = useState(null);
  const [reporting, setReporting] = useState({}); // { [reviewId]: true/false }
  const [reported, setReported] = useState({}); // { [reviewId]: true/false }
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      setError(null);
      try {
        const allBusinesses = await businessApi.fetchAll();
        const myBusinesses = allBusinesses.filter(
          b => String(b.userId) === String(user.id) ||
               String(b.userid) === String(user.id) ||
               String(b.ownerId) === String(user.id) ||
               String(b.ownerid) === String(user.id)
        );
        let allReviews = [];
        for (const business of myBusinesses) {
          const businessReviews = await reviewsApi.getByBusinessId(business.id);
          if (Array.isArray(businessReviews)) {
            allReviews = allReviews.concat(
              businessReviews.map(r => ({
                ...r,
                businessName: business.name,
                businessCategory: business.category,
                businessId: business.id
              }))
            );
          }
        }
        setReviews(allReviews);
      } catch (err) {
        setError('Failed to load reviews.');
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    if (user && user.id) fetchReviews();
  }, [user]);

  // Date filter logic
  const filteredReviews = reviews.filter(r => {
    if (dateFrom && new Date(r.date) < new Date(dateFrom)) return false;
    if (dateTo && new Date(r.date) > new Date(dateTo)) return false;
    return true;
  });

  // Sorting logic
  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (sortBy === 'date-desc') return new Date(b.date) - new Date(a.date);
    if (sortBy === 'date-asc') return new Date(a.date) - new Date(b.date);
    if (sortBy === 'rating-desc') return (b.rating || 0) - (a.rating || 0);
    if (sortBy === 'rating-asc') return (a.rating || 0) - (b.rating || 0);
    return 0;
  });

  // Reply modal handlers
  const openReplyModal = (reviewId) => {
    setReplyingReviewId(reviewId);
    setReplyText('');
    setShowReplyModal(true);
    setSuccessMsg('');
    setErrorMsg('');
  };
  const closeReplyModal = () => {
    setShowReplyModal(false);
    setReplyingReviewId(null);
    setReplyText('');
  };
  const handleReplySubmit = async () => {
    if (!replyText.trim()) {
      setErrorMsg('Reply cannot be empty.');
      return;
    }
    try {
      setErrorMsg('');
      setSuccessMsg('');
      await reviewsApi.replyToReview(replyingReviewId, replyText);
      setSuccessMsg('Reply sent successfully!');
      setTimeout(() => {
        closeReplyModal();
      }, 1000);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to send reply.');
    }
  };

  // Report handler
  const handleReport = async (reviewId) => {
    setReporting(r => ({ ...r, [reviewId]: true }));
    setErrorMsg('');
    setSuccessMsg('');
    try {
      await reviewsApi.reportReview(reviewId);
      setReported(r => ({ ...r, [reviewId]: true }));
      setSuccessMsg('Review reported.');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to report review.');
    } finally {
      setReporting(r => ({ ...r, [reviewId]: false }));
    }
  };

  return (
    <DashboardLayout>
      <div>
        <h2 className="mb-4">Reviews</h2>
        <Form className="mb-4">
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Group controlId="dateFrom">
                <Form.Label>From</Form.Label>
                <Form.Control type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="dateTo">
                <Form.Label>To</Form.Label>
                <Form.Control type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="sortBy">
                <Form.Label>Sort By</Form.Label>
                <Form.Select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="rating-desc">Highest Rated</option>
                  <option value="rating-asc">Lowest Rated</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
        {successMsg && <Alert variant="success">{successMsg}</Alert>}
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        {loading ? (
          <div className="text-center my-5"><Spinner animation="border" /></div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : sortedReviews.length === 0 ? (
          <Alert variant="info">No reviews found for your businesses.</Alert>
        ) : (
          <Row className="g-4">
            {sortedReviews.map((review, idx) => (
              <Col md={6} key={review.id || idx}>
                <Card className="shadow-sm h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <Badge bg="secondary" className="me-2">{review.businessName}</Badge>
                        <span className="fw-bold">{review.reviewer || 'Anonymous'}</span>
                        {review.email && <span className="text-muted small ms-2">Email: {review.email}</span>}
                        {review.businessCategory && <span className="text-muted small ms-2">Category: {review.businessCategory}</span>}
                        <span className="text-muted small ms-2">ID: {review.id}</span>
                      </div>
                      <span className="text-muted small">{review.date ? new Date(review.date).toLocaleDateString() : ''}</span>
                    </div>
                    <div className="mb-2">
                      {[...Array(Number(review.rating || 0))].map((_, i) => <FaStar key={i} className="text-warning" />)}
                    </div>
                    <div className="mb-3">{review.text}</div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-success" size="sm" onClick={() => openReplyModal(review.id)}>
                        <FaReply className="me-1" /> Reply
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={reported[review.id] || reporting[review.id]}
                        onClick={() => handleReport(review.id)}
                      >
                        <FaFlag className="me-1" />
                        {reported[review.id] ? 'Reported' : reporting[review.id] ? 'Reporting...' : 'Report'}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        {/* Reply Modal */}
        <Modal show={showReplyModal} onHide={closeReplyModal}>
          <Modal.Header closeButton>
            <Modal.Title>Reply to Review</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control
              as="textarea"
              rows={3}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Type your reply here..."
            />
            {errorMsg && <Alert variant="danger" className="mt-2">{errorMsg}</Alert>}
            {successMsg && <Alert variant="success" className="mt-2">{successMsg}</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeReplyModal}>Cancel</Button>
            <Button variant="primary" onClick={handleReplySubmit}>Send Reply</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default DashboardReviews; 